import React, { useState, useEffect, useRef } from 'react';
import { getTalkliveInstances, getAllDealerAssistant } from '../services/api';
import TalkliveInstanceDropdown from './TalkliveInstanceDropdown';
import {
  getInstanceStorageValue,
  getInstanceDisplayLabel,
  normalizeTalkliveInstancesResponse,
} from '../utils/talkliveInstanceUtils';

const TalkliveInstancesAccessSection = ({
  isOpen,
  assistantsAccess,
  instances,
  onInstancesChange,
}) => {
  const [availableOptions, setAvailableOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const assistantNamesRef = useRef({});

  const assistantIds = [...new Set(
    (assistantsAccess || []).filter((id) => id?.trim())
  )];

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadAssistantNames = async () => {
      try {
        const response = await getAllDealerAssistant();
        const assistants = response.data?.data || [];
        assistantNamesRef.current = Object.fromEntries(
          assistants.map((a) => [a.assistant_id, a.name || a.assistant_id])
        );
      } catch (err) {
        console.error('Error fetching assistant names:', err);
      }
    };

    const loadInstances = async () => {
      setLoading(true);

      if (assistantIds.length === 0) {
        if (!cancelled) {
          setAvailableOptions([]);
          setLoading(false);
          if (instances.length > 0) {
            onInstancesChange([]);
          }
        }
        return;
      }

      await loadAssistantNames();

      try {
        const results = await Promise.allSettled(
          assistantIds.map((id) => getTalkliveInstances(id))
        );

        if (cancelled) return;

        const optionMap = new Map();

        results.forEach((result, index) => {
          if (result.status !== 'fulfilled') return;

          const assistantId = assistantIds[index];
          const assistantName = assistantNamesRef.current[assistantId] || assistantId;
          const instanceList = normalizeTalkliveInstancesResponse(result.value.data);

          instanceList.forEach((instance) => {
            const storageValue = getInstanceStorageValue(instance);
            if (!storageValue || optionMap.has(storageValue)) return;

            optionMap.set(storageValue, {
              value: storageValue,
              label: getInstanceDisplayLabel(instance),
              sublabel: assistantName,
            });
          });
        });

        const options = [...optionMap.values()].sort((a, b) =>
          a.label.localeCompare(b.label)
        );

        setAvailableOptions(options);

        const validIds = new Set(options.map((o) => o.value));
        const pruned = instances.filter((id) => validIds.has(id));
        if (pruned.length !== instances.length) {
          onInstancesChange(pruned);
        }
      } catch (err) {
        console.error('Error fetching TalkLive instances:', err);
        if (!cancelled) {
          setAvailableOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadInstances();

    return () => {
      cancelled = true;
    };
  }, [isOpen, assistantIds.join(',')]);

  const handleInstanceChange = (index, value) => {
    onInstancesChange(
      instances.map((instance, i) => (i === index ? value : instance))
    );
  };

  const addInstance = () => {
    onInstancesChange([...instances, '']);
  };

  const removeInstance = (index) => {
    onInstancesChange(instances.filter((_, i) => i !== index));
  };

  const getExcludeValues = (currentIndex) =>
    instances.filter((id, i) => i !== currentIndex && id);

  const selectedCount = instances.filter(Boolean).length;
  const canAddInstance =
    !loading &&
    assistantIds.length > 0 &&
    availableOptions.length > 0 &&
    selectedCount < availableOptions.length;

  return (
    <div className="form-group">
      <label>TalkLive Instances Access:</label>
      <div className="assistants-container">
        {loading && instances.length === 0 && (
          <div className="instances-helper-text">Loading instances...</div>
        )}
        {!loading && assistantIds.length === 0 && (
          <div className="instances-helper-text">
            Add assistants above to configure TalkLive instance access.
          </div>
        )}
        {!loading && assistantIds.length > 0 && availableOptions.length === 0 && (
          <div className="instances-helper-text">
            No TalkLive instances found for the selected assistants.
          </div>
        )}
        {instances.map((instance, index) => (
          <div key={index} className="assistant-input-row">
            <TalkliveInstanceDropdown
              value={instance}
              onChange={(value) => handleInstanceChange(index, value)}
              options={availableOptions}
              excludeValues={getExcludeValues(index)}
              placeholder="Select instance..."
              loading={loading}
              disabled={loading || availableOptions.length === 0}
            />
            <button
              type="button"
              onClick={() => removeInstance(index)}
              className="remove-assistant-btn"
            >
              -
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addInstance}
          className="add-assistant-btn"
          disabled={!canAddInstance}
        >
          + Add Instance
        </button>
      </div>
    </div>
  );
};

export default TalkliveInstancesAccessSection;
